/**
 * BFF client for the authenticator-enrollment-invitation flow.
 *
 * The user-portal-api BFF already shapes responses to match
 * ``OnboardingFlowResponse`` from the onboarding feature, so the factor flow
 * can reuse all of the onboarding view components via dependency-injected API.
 * Exports:
 *   - ``startInvitation``           — validates the magic token, starts the flow
 *   - ``enrollmentInvitationApiClient`` — the ``OnboardingApiClient`` adapter
 *   - ``completeInvitationFlow``    — final /complete call; the hooks invoke
 *     this via ``OnboardingApiClient.completeOnboarding``
 */

// TODO(cross-feature-reuse): lift OnboardingApiClient + OnboardingFlowResponse
// into @/types (or @/api) so the enrollment-invitation feature consumes them
// without the boundaries rule being disabled. Tracked as the follow-up called
// out in ``enrollment-invitation-page.tsx``.
/* eslint-disable boundaries/element-types */
import { getTenantFromPath } from '@/common/tenant-utils';
import type {
  OnboardingApiAlternative,
  OnboardingApiClient,
} from '@/features/onboarding/api/onboarding-api-client';
import type { OnboardingFlowResponse } from '@/features/onboarding/types/onboarding.types';
/* eslint-enable boundaries/element-types */

const BASE_PATH = '/user-portal-api/enrollment-invitation';

export interface InvitationStartResponse extends OnboardingFlowResponse {
  valid: boolean;
  invitation_id?: string;
  requestedFactors?: string[];
}

// Auto-prefix paths with the tenant slug pulled from window.location — traefik
// routes `/{tenant}/user-portal-api/...` to the BFF, so anonymous fetches
// must include the tenant segment or traefik's auth middleware returns 401.
function buildUrl(path: string): string {
  const tenant = getTenantFromPath();
  const fullPath = `${BASE_PATH}${path}`;

  return tenant ? `/${tenant}${fullPath}` : fullPath;
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  const response = await fetch(buildUrl(path), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    throw new Error(
      `POST ${path} failed: ${response.status} ${response.statusText}`
    );
  }

  return (await response.json()) as T;
}

async function get<T>(path: string): Promise<T> {
  const response = await fetch(buildUrl(path), {
    method: 'GET',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(
      `GET ${path} failed: ${response.status} ${response.statusText}`
    );
  }

  return (await response.json()) as T;
}

export async function startInvitation(
  magicToken: string
): Promise<InvitationStartResponse> {
  return post<InvitationStartResponse>('/start', { magic_token: magicToken });
}

function submitFactor(
  payload: Record<string, unknown>
): Promise<OnboardingFlowResponse> {
  return post<OnboardingFlowResponse>('/submit', { factor_payload: payload });
}

function submitPassword(password: string): Promise<OnboardingFlowResponse> {
  return submitFactor({ password });
}

function submitSmsCode(code: string): Promise<OnboardingFlowResponse> {
  return submitFactor({ otp_code: code });
}

function resendOtp(): Promise<{ success: boolean }> {
  // No BFF resend endpoint for this flow. The invitation wizard intentionally
  // omits ``onResend`` when rendering ``FactorSteps``, so this path is
  // unreachable from the UI. Kept only to satisfy the ``OnboardingApiClient``
  // interface shared with onboarding.
  return Promise.resolve({ success: false });
}

function getAuthenticatorAlternatives(): Promise<{
  alternatives: OnboardingApiAlternative[];
}> {
  return get<{ alternatives: OnboardingApiAlternative[] }>(
    '/authenticator-alternatives'
  );
}

function chooseAuthenticator(
  factorType: string
): Promise<OnboardingFlowResponse> {
  return post<OnboardingFlowResponse>('/choose-authenticator', {
    authenticator: factorType,
  });
}

function completeOnboarding(): Promise<OnboardingFlowResponse> {
  return post<OnboardingFlowResponse>('/complete');
}

export const enrollmentInvitationApiClient: OnboardingApiClient = {
  submitFactor,
  submitPassword,
  submitSmsCode,
  resendOtp,
  getAuthenticatorAlternatives,
  chooseAuthenticator,
  completeOnboarding,
};
